// Каталог растворов — CRUD над `/api/public/muds`. Тот же паттерн что у
// CasingsPage, но больше полей: 8 theta-показаний FANN + 2 gel + 3 HB-параметра
// + плотность + estimation_method.
//
// estimation_method — селектор `user`/`simple`/`mullinex`. При smith/mullinex HB
// сейчас просто хранятся как заданы (сервер не пересчитывает) — поэтому
// валидно ввести любые числа.

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listMuds,
  createMud,
  updateMud,
  deleteMud,
  InUseError,
  ESTIMATION_METHODS,
} from './api/muds'
import type { MudDto } from './api/muds'

// ────────────────────── редактор ──────────────────────

const NUMERIC_KEYS = [
  'density_kg_m3',
  'theta_o600', 'theta_o300', 'theta_o200', 'theta_o100',
  'theta_o60',  'theta_o30',  'theta_o6',   'theta_o3',
  'gel_10_min', 'gel_10_sec',
  'hb_tau_y',   'hb_k',       'hb_n',
] as const
type NumKey = typeof NUMERIC_KEYS[number]

type EditorState = {
  source:            MudDto | null
  name:              string
  description:       string
  estimation_method: string
  /// Все числа как строки — парсятся на submit.
  vals:              Record<NumKey, string>
}

function emptyVals(): Record<NumKey, string> {
  const v = {} as Record<NumKey, string>
  for (const k of NUMERIC_KEYS) v[k] = ''
  return v
}

function emptyEditor(): EditorState {
  return {
    source:            null,
    name:              '',
    description:       '',
    estimation_method: 'user',
    vals:              emptyVals(),
  }
}

function editorFrom(m: MudDto): EditorState {
  const vals = {} as Record<NumKey, string>
  for (const k of NUMERIC_KEYS) vals[k] = String(m[k])
  return {
    source:            m,
    name:              m.name,
    description:       m.description,
    estimation_method: m.estimation_method,
    vals,
  }
}

const THETA_GRID: { key: NumKey; label: string }[] = [
  { key: 'theta_o600', label: 'θ₆₀₀' },
  { key: 'theta_o300', label: 'θ₃₀₀' },
  { key: 'theta_o200', label: 'θ₂₀₀' },
  { key: 'theta_o100', label: 'θ₁₀₀' },
  { key: 'theta_o60',  label: 'θ₆₀'  },
  { key: 'theta_o30',  label: 'θ₃₀'  },
  { key: 'theta_o6',   label: 'θ₆'   },
  { key: 'theta_o3',   label: 'θ₃'   },
]

const GEL_GRID: { key: NumKey; label: string; hint: string }[] = [
  { key: 'gel_10_sec', label: 'gel 10c', hint: '10 секунд' },
  { key: 'gel_10_min', label: 'gel 10м', hint: '10 минут'  },
]

const HB_GRID: { key: NumKey; label: string; hint: string }[] = [
  { key: 'hb_tau_y', label: 'τ_y', hint: 'предел текучести, Pa' },
  { key: 'hb_k',     label: 'K',   hint: 'консистенция, Pa·sⁿ'  },
  { key: 'hb_n',     label: 'n',   hint: 'индекс поведения'     },
]

type EditorProps = {
  state:   EditorState
  onClose: () => void
  onSaved: () => void
}

const Editor: Component<EditorProps> = (props) => {
  const initial = props.state
  const [st, setSt] = createSignal<EditorState>(props.state)
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const isCreate = () => st().source === null

  const isDirty = createMemo<boolean>(() => {
    const c = st()
    if (isCreate()) {
      if (c.name.trim().length > 0) return true
      if (c.description.trim().length > 0) return true
      if (c.estimation_method !== initial.estimation_method) return true
      return Object.values(c.vals).some((v) => v.trim().length > 0)
    }
    if (c.name !== initial.name) return true
    if (c.description !== initial.description) return true
    if (c.estimation_method !== initial.estimation_method) return true
    for (const k of NUMERIC_KEYS) {
      if (c.vals[k] !== initial.vals[k]) return true
    }
    return false
  })

  const tryClose = () => {
    if (busy()) return
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Закрыть?')) return
    props.onClose()
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        tryClose()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  const updateVal = (k: NumKey, val: string) =>
    setSt({ ...st(), vals: { ...st().vals, [k]: val } })

  const onSubmit = async (e: Event) => {
    e.preventDefault()
    setErr(null)
    const cur = st()
    const nameTrimmed = cur.name.trim()
    if (nameTrimmed.length === 0) {
      setErr('Название обязательно')
      return
    }

    // Парсим: пустое → 0 (валидно), нечисловое → ошибка. Density обязательна > 0.
    const parsed = {} as Record<NumKey, number>
    for (const k of NUMERIC_KEYS) {
      const raw = cur.vals[k]?.trim() ?? ''
      const n = raw === '' ? 0 : Number(raw)
      if (!Number.isFinite(n)) {
        setErr(`Поле ${k}: ожидается число`)
        return
      }
      parsed[k] = n
    }
    if (parsed.density_kg_m3 <= 0) {
      setErr('Плотность должна быть > 0')
      return
    }

    setBusy(true)
    try {
      const body = {
        name:              nameTrimmed,
        description:       cur.description,
        density_kg_m3:     parsed.density_kg_m3,
        estimation_method: cur.estimation_method,
        theta_o600:        parsed.theta_o600,
        theta_o300:        parsed.theta_o300,
        theta_o200:        parsed.theta_o200,
        theta_o100:        parsed.theta_o100,
        theta_o60:         parsed.theta_o60,
        theta_o30:         parsed.theta_o30,
        theta_o6:          parsed.theta_o6,
        theta_o3:          parsed.theta_o3,
        gel_10_min:        parsed.gel_10_min,
        gel_10_sec:        parsed.gel_10_sec,
        hb_tau_y:          parsed.hb_tau_y,
        hb_k:              parsed.hb_k,
        hb_n:              parsed.hb_n,
      }
      if (isCreate()) await createMud(body)
      else            await updateMud(cur.source!.id, body)
      props.onSaved()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  const renderCell = (item: { key: NumKey; label: string; hint?: string }) => (
    <div class="dim-cell">
      <div class="dim-label">
        <span class="dim-symbol">{item.label}</span>
        <Show when={item.hint}>
          <span class="dim-hint">{item.hint}</span>
        </Show>
      </div>
      <input
        class="field-input mono"
        value={st().vals[item.key]}
        onInput={(e) => updateVal(item.key, e.currentTarget.value)}
        inputmode="decimal"
        placeholder="0"
      />
    </div>
  )

  return (
    <form class="equipment-editor" onSubmit={onSubmit}>
      <div class="equipment-editor-head">
        <button class="btn btn-ghost" type="button" onClick={() => tryClose()} disabled={busy()}>
          ← К каталогу
        </button>
        <div class="equipment-editor-title">
          <div class="uppercase">{isCreate() ? 'Новый раствор' : 'Редактирование'}</div>
          <h1>{isCreate() ? 'Создать раствор' : st().name || 'Раствор'}</h1>
        </div>
        <div class="equipment-editor-actions">
          <button class="btn" type="button" onClick={() => tryClose()} disabled={busy()}>
            Отмена
          </button>
          <button class="btn btn-primary" type="submit" disabled={busy() || !isDirty()}>
            {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>

      <Show when={err()}>
        <div class="card equipment-error">{err()}</div>
      </Show>

      <main class="equipment-form-panel">
        <section class="equipment-form-section">
          <div class="equipment-form-main">
            <label class="field">
              <div class="field-label">Название</div>
              <input
                class="field-input"
                value={st().name}
                onInput={(e) => setSt({ ...st(), name: e.currentTarget.value })}
                placeholder="Напр., Полимер-глинистый 1.18 г/см³"
                autofocus
              />
            </label>

            <label class="field">
              <div class="field-label">Описание</div>
              <textarea
                class="field-input"
                rows={3}
                value={st().description}
                onInput={(e) => setSt({ ...st(), description: e.currentTarget.value })}
                placeholder="Произвольное примечание"
              />
            </label>

            <div class="row" style={{ gap: 'var(--sp-4)' }}>
              <label class="field" style={{ flex: 1 }}>
                <div class="field-label">Плотность, кг/м³</div>
                <input
                  class="field-input mono"
                  value={st().vals.density_kg_m3}
                  onInput={(e) => updateVal('density_kg_m3', e.currentTarget.value)}
                  inputmode="decimal"
                  placeholder="1180"
                />
              </label>

              <label class="field" style={{ flex: 1 }}>
                <div class="field-label">Метод HB-оценки</div>
                <select
                  class="field-input"
                  value={st().estimation_method}
                  onChange={(e) => setSt({ ...st(), estimation_method: e.currentTarget.value })}
                >
                  <For each={ESTIMATION_METHODS}>
                    {(m) => <option value={m.id}>{m.label}</option>}
                  </For>
                </select>
              </label>
            </div>
          </div>

          <div class="field">
            <div class="field-label">Реология FANN (lb/100ft²)</div>
            <div class="dim-grid equipment-dim-grid">
              <For each={THETA_GRID}>{renderCell}</For>
            </div>
          </div>

          <div class="field">
            <div class="field-label">Gel breaks</div>
            <div class="dim-grid equipment-dim-grid">
              <For each={GEL_GRID}>{renderCell}</For>
            </div>
          </div>

          <div class="field">
            <div class="field-label">Herschel-Bulkley (используется при method='user')</div>
            <div class="dim-grid equipment-dim-grid">
              <For each={HB_GRID}>{renderCell}</For>
            </div>
          </div>
        </section>
      </main>
    </form>
  )
}

// ────────────────────── страница ──────────────────────

type MudsPageProps = {
  mudId?:   number | null
  create?:  boolean
  onOpen:   (id: number) => void
  onCreate: () => void
  onBack:   () => void
}

export const MudsPage: Component<MudsPageProps> = (props) => {
  const [items, { refetch }] = createResource(() => listMuds())
  const pageItems = createMemo<MudDto[]>(() => items()?.items ?? [])
  const [delErr, setDelErr] = createSignal<string | null>(null)
  const [filterText, setFilterText] = createSignal('')

  const editor = createMemo<EditorState | null>(() => {
    if (props.create) return emptyEditor()
    if (props.mudId == null) return null
    const item = pageItems().find((m) => m.id === props.mudId)
    return item ? editorFrom(item) : null
  })

  const filtered = createMemo<MudDto[]>(() => {
    const all = pageItems()
    const q = filterText().trim().toLowerCase()
    if (q.length === 0) return all
    return all.filter((m) =>
      m.name.toLowerCase().includes(q)
      || (m.description ?? '').toLowerCase().includes(q)
    )
  })

  const openCreate = () => props.onCreate()
  const openEdit = (m: MudDto) => props.onOpen(m.id)
  const closeEditor = () => props.onBack()
  const onSaved = () => {
    refetch()
    props.onBack()
  }

  const onDelete = async (m: MudDto) => {
    if (!confirm(`Удалить «${m.name}»?`)) return
    setDelErr(null)
    try {
      await deleteMud(m.id)
      refetch()
    } catch (err) {
      if (err instanceof InUseError) setDelErr(err.message)
      else setDelErr(String(err))
    }
  }

  const methodLabel = (id: string): string =>
    ESTIMATION_METHODS.find((m) => m.id === id)?.label ?? id

  // Краткая характеристика реологии — θ600/θ300, для быстрой ориентировки в табличке.
  const rheologySummary = (m: MudDto): string =>
    `θ600=${m.theta_o600} · θ300=${m.theta_o300}`

  return (
    <Show
      when={editor()}
      keyed
      fallback={<div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Каталог растворов</h1>
          <p class="page-subtitle">
            Рецепты бурового раствора — реология FANN, плотность, метод HB-оценки.
            MD-точка и scope (внутри/снаружи) задаются при использовании в state.
          </p>
        </div>
        <div class="row">
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Раствор
          </button>
        </div>
      </div>

      <Show when={delErr()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {delErr()}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.total === 0}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Каталог пуст
          </div>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Создайте первый рецепт раствора
          </div>
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Добавить раствор
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.total > 0}>
        <div class="row filter-row" style={{ gap: 'var(--sp-3)', 'margin-bottom': 'var(--sp-4)' }}>
          <div class="input" style={{ flex: 1, 'max-width': '320px' }}>
            <Icon name="search" />
            <input
              placeholder="Поиск по названию / описанию"
              value={filterText()}
              onInput={(e) => setFilterText(e.currentTarget.value)}
            />
            <Show when={filterText().length > 0}>
              <button
                class="btn-icon btn-ghost"
                style={{ width: '24px', height: '24px' }}
                title="Очистить"
                onClick={() => setFilterText('')}
              >
                <Icon name="x" size={12} />
              </button>
            </Show>
          </div>
          <div class="muted text-sm filter-counter" style={{ 'margin-left': 'auto', 'align-self': 'center' }}>
            Показано {filtered().length} из {items()!.total}
          </div>
        </div>

        <div class="card">
          <table class="tbl">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>ID</th>
                <th>Название</th>
                <th>ρ, кг/м³</th>
                <th>Реология</th>
                <th>Метод HB</th>
                <th>Описание</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              <For each={filtered()}>
                {(m) => (
                  <tr style={{ cursor: 'pointer' }} onClick={() => openEdit(m)}>
                    <td class="mono text-xs muted">#{m.id}</td>
                    <td style={{ 'font-weight': 500 }}>{m.name}</td>
                    <td class="mono text-xs">{m.density_kg_m3}</td>
                    <td class="mono text-xs">{rheologySummary(m)}</td>
                    <td class="text-xs">{methodLabel(m.estimation_method)}</td>
                    <td class="text-xs muted">{m.description || '—'}</td>
                    <td>
                      <button
                        class="btn-icon"
                        title="Удалить"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onDelete(m)
                        }}
                      >
                        <Icon name="x" />
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={filtered().length === 0}>
            <div style={{ padding: 'var(--sp-5)', 'text-align': 'center' }} class="muted text-sm">
              Ничего не найдено по фильтру
            </div>
          </Show>
        </div>
      </Show>

      </div>}
    >
      {(ed) => (
        <div class="page page-wide">
          <Editor state={ed} onClose={closeEditor} onSaved={onSaved} />
        </div>
      )}
    </Show>
  )
}
